"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import {
  AnimatePresence,
  motion,
  useScroll,
  useMotionValueEvent,
} from "framer-motion";
import {
  ShoppingCart,
  User,
  ChevronDown,
  ChevronRight,
  UtensilsCrossed,
  Package,
  Truck,
  FileText,
  Shield,
  Info,
  Tractor,
} from "lucide-react";

import { PALETTE } from "../lib/colorPallete";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import ProfileDropdown from "./ProfileDropdown";
import { useOutsideClick } from "../hooks/useOutsideClick";
import { WebAppNavbarProps } from "../types/types";
import { NavLink } from "./ui/Navlink";
type NavLinkItem = {
  type: "link";
  title: string;
  href: string;
};

type MegaMenuItem = {
  type: "mega-menu";
  title: string;
  href: string;
  subItems: {
    title: string;
    href: string;
    icon: React.ReactNode;
    description: string;
  }[];
  featured: {
    title: string;
    description: string;
    href: string;
    image: string;
  };
};

type NavItem = NavLinkItem | MegaMenuItem;
const navItems: NavItem[] = [
  {
    type: "mega-menu",
    title: "Products",
    href: "/",
    subItems: [
      {
        title: "Agriculture Sprayers",
        href: `/products/Agriculture Sprayers?page=1&sortBy=createdAt&sortOrder=desc`,
        icon: <Tractor size={18} />,
        description: "Advanced spraying solutions",
      },
      {
        title: "Water Pumps",
        href: `/products/Water Pumps?page=1&sortBy=createdAt&sortOrder=desc`,
        icon: <Tractor size={18} />,
        description: "Pumps for every application",
      },
      {
        title: "Agriculture Equipments",
        href: `/products/Agriculture Equipments?page=1&sortBy=createdAt&sortOrder=desc`,
        icon: <Tractor size={18} />,
        description: "Chaff cutters, brush cutters, etc",
      },
      {
        title: "Food Processing",
        href: "/products/Food Processing?page=1&sortBy=createdAt&sortOrder=desc",
        icon: <UtensilsCrossed size={18} />,
        description: "Noodle, peanut, garlic machinery",
      },
      {
        title: "Spare Parts",
        href: `/products/spare%20parts?page=1&sortBy=createdAt&sortOrder=desc`,
        icon: <Package size={18} />,
        description: "Guns, nozzles, lances and more",
      },
      {
        title: "Herbal Powder",
        href: "/products/herbal powders?page=1&sortBy=createdAt&sortOrder=desc",
        icon: <Tractor size={18} />,
        description: "Herbal powder for various applications",
      }
    ],
    featured: {
      title: "Shop Now",
      description: "Explore premium quality equipment",
      href: "/",
      image: "/logo-tsp.png",
    },
  },
  {
    type: "mega-menu",
    title: "Company",
    href: "/company",
    subItems: [
      {
        title: "About Us",
        href: "/about",
        icon: <Info size={18} />,
        description: "Learn about our journey and mission",
      },
      {
        title: "Our Values",
        href: "/values",
        icon: (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 2L2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5" />
            <path d="M2 12l10 5 10-5" />
          </svg>
        ),
        description: "Our core principles and commitments",
      },
      {
        title: "Certificates",
        href: "/certificates",
        icon: (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
            <polyline points="14 2 14 8 20 8" />
            <path d="M9 15h6" />
          </svg>
        ),
        description: "Quality certifications and standards",
      },
      {
        title: "Exhibitions",
        href: "/exhibitions",
        icon: (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
          </svg>
        ),
        description: "Events and trade shows we attend",
      },
    ],
    featured: {
      title: "Our Story",
      description: "Excellence in agricultural equipment",
      href: "/company",
      image: "/logo-tsp.png",
    },
  },
  { type: "link", title: "Blogs", href: "/blogs" },
  { type: "link", title: "Contact", href: "/contact" },
  { type: "link", title: "Orders", href: "/orders" },
  {
    type: "mega-menu",
    title: "Policies",
    href: "/shipping",
    subItems: [
      {
        title: "Shipping & Returns",
        href: "/shipping",
        icon: <Truck size={18} />,
        description: "Delivery and return policies",
      },
      {
        title: "Terms & Conditions",
        href: "/terms",
        icon: <FileText size={18} />,
        description: "Terms of service and usage",
      },
      {
        title: "Privacy Policy",
        href: "/privacy",
        icon: <Shield size={18} />,
        description: "How we protect your data",
      },
    ],
    featured: {
      title: "Support Center",
      description: "Find answers to common questions",
      href: "/shipping",
      image: "/logo-tsp.png",
    },
  },
];

const WebAppNavbar: React.FC<WebAppNavbarProps> = ({ onCartClick }) => {
  const { itemCount } = useCart();
  const { user, logout, openAuthModal } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [expandedMobileMenu, setExpandedMobileMenu] = useState<string | null>(
    null,
  );
  const pathname = usePathname();

  useMotionValueEvent(useScroll().scrollY, "change", (latest) =>
    setScrolled(latest > 50),
  );

  useEffect(() => {
    if (!isMobileMenuOpen) {
      setExpandedMobileMenu(null);
    }
  }, [isMobileMenuOpen]);

  const profileRef = useOutsideClick<HTMLDivElement>(() => {
    setIsProfileOpen(false);
  });

  const handleProfileClick = () => {
    if (user) {
      setIsProfileOpen((prev) => !prev);
    } else {
      openAuthModal();
    }
  };

  const activePillId = "nav-active-pill";
  const navTextColor = scrolled ? PALETTE.darkGreen : PALETTE.darkerGreen;

  return (
    <>
      <motion.header
        className="fixed top-0 left-0 w-full z-50"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 100, damping: 20, delay: 0.5 }}
        style={{
          backgroundColor: scrolled
            ? `${PALETTE.cream}E6`
            : `${PALETTE.cream}E6`,
          backdropFilter: scrolled ? "blur(12px)" : "none",
          WebkitBackdropFilter: scrolled ? "blur(12px)" : "none",
          borderBottom: scrolled
            ? `1px solid ${PALETTE.darkGreen}20`
            : "1px solid transparent",
          transition: "background-color 0.4s ease, border-color 0.4s ease",
        }}
      >
        <div
          className="mx-auto flex w-full items-center justify-between px-4 sm:px-6 lg:px-8 gap-4"
          style={{
            height: scrolled
              ? "var(--navbar-height-scrolled, 70px)"
              : "var(--navbar-height, 110px)",
            transition: "height 0.3s ease",
          }}
        >
          { }
          <div className="shrink-0">
            <Link href="/" className="inline-block">
              <Image
                src={"/logo-tsp.png"}
                alt="logo"
                height={110}
                width={110}
                priority
                className="transition-all duration-300 mt-3"
                style={{
                  width: !scrolled ? "150px" : "150px",
                }}
              />
            </Link>
          </div>

          { }
          <nav
            className="hidden lg:flex items-center justify-center gap-1 rounded-full p-1.5 flex-1 max-w-3xl mx-auto relative"
            style={{
              backgroundColor: scrolled
                ? "rgba(255,255,255,0.5)"
                : "transparent",
              borderColor: scrolled ? "rgba(0,0,0,0.1)" : "transparent",
              transition: "background-color 0.4s ease, border-color 0.4s ease",
            }}
            onMouseLeave={() => setHoveredItem(null)}
          >
            {navItems.map((item) => {
              if (item.type === "link") {
                const isActive =
                  item.href === "/orders"
                    ? pathname === "/orders"
                    : item.href === "/blogs"
                      ? pathname === "/blogs"
                      : item.href === "/contact"
                        ? pathname === "/contact"
                        : pathname.startsWith(item.href);
                return (
                  <NavLink
                    key={item.title}
                    href={item.href}
                    label={item.title}
                    isActive={isActive}
                    activePillId={activePillId}
                  />
                );
              }
              if (item.type === "mega-menu") {
                const isActive =
                  (item.href === "/" && pathname === "/") ||
                  (item.href === "/company" && pathname === "/company") ||
                  item.subItems.some((sub) => pathname.startsWith(sub.href));
                return (
                  <div
                    key={item.title}
                    className="relative"
                    onMouseEnter={() => setHoveredItem(item.title)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <Link
                      href={item.href}
                      className="relative px-3 py-1.5 text-base font-medium flex items-center gap-1 cursor-pointer"
                      style={{ color: navTextColor }}
                    >
                      {item.title}
                      <ChevronDown
                        size={16}
                        className={`transition-transform ${hoveredItem === item.title ? "rotate-180" : ""
                          }`}
                      />
                      {isActive && (
                        <motion.span
                          layoutId={activePillId}
                          className="absolute inset-0 z-[-1] rounded-full"
                          style={{ backgroundColor: `${PALETTE.gold}60` }}
                        />
                      )}
                    </Link>
                    <AnimatePresence>
                      {hoveredItem === item.title && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="absolute left-0 top-full pt-3 z-50"
                          style={{
                            minWidth: "500px",
                            transform: "translateX(-25%)",
                          }}
                        >
                          <MegaMenuContent menu={item} />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }
              return null;
            })}
          </nav>

          { }
          <div className="flex items-center justify-end gap-1 sm:gap-2 shrink-0 relative z-60">
            { }
            <motion.button
              onClick={onCartClick}
              className="relative p-2 rounded-full transition-colors hover:bg-stone-200 z-60"
              whileTap={{ scale: 0.9 }}
            >
              <ShoppingCart size={24} style={{ color: navTextColor }} />
              <AnimatePresence>
                {itemCount > 0 && (
                  <motion.div
                    key={itemCount}
                    initial={{ scale: 0, y: 10 }}
                    animate={{ scale: 1, y: 0 }}
                    exit={{ scale: 0 }}
                    transition={{ type: "spring", stiffness: 500, damping: 20 }}
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{
                      backgroundColor: PALETTE.gold,
                      color: PALETTE.darkerGreen,
                    }}
                  >
                    {itemCount}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>

            { }
            <div ref={profileRef} className="relative">
              <button
                onClick={handleProfileClick}
                className="p-2 rounded-full transition-colors hover:bg-stone-200"
              >
                <User size={24} style={{ color: navTextColor }} />
              </button>
              <AnimatePresence>
                {isProfileOpen && user && (
                  <ProfileDropdown
                    user={user}
                    logout={logout}
                    onClose={() => setIsProfileOpen(false)}
                  />
                )}
              </AnimatePresence>
            </div>

            { }
            <button
              className="block lg:hidden z-50 p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <motion.path
                  d="M4 18L20 18"
                  stroke={navTextColor}
                  strokeWidth="2"
                  strokeLinecap="round"
                  animate={{
                    d: isMobileMenuOpen ? "M6 18L18 6" : "M4 18L20 18",
                  }}
                />
                <motion.path
                  d="M4 12L20 12"
                  stroke={navTextColor}
                  strokeWidth="2"
                  strokeLinecap="round"
                  animate={{ opacity: isMobileMenuOpen ? 0 : 1 }}
                  transition={{ duration: 0.2 }}
                />
                <motion.path
                  d="M4 6L20 6"
                  stroke={navTextColor}
                  strokeWidth="2"
                  strokeLinecap="round"
                  animate={{ d: isMobileMenuOpen ? "M6 6L18 18" : "M4 6L20 6" }}
                />
              </svg>
            </button>
          </div>
        </div>
      </motion.header>

      { }
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden overflow-y-auto"
            style={{
              backgroundColor: `${PALETTE.cream}`,
              paddingTop: "110px",
            }}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <motion.div
              className="w-full min-h-full px-4 py-6"
              variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
              initial="hidden"
              animate="visible"
              exit="hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {navItems.map((item) => {
                if (item.type === "link") {
                  const isActive =
                    item.href === "/orders"
                      ? pathname === "/orders"
                      : item.href === "/blogs"
                        ? pathname === "/blogs"
                        : item.href === "/contact"
                          ? pathname === "/contact"
                          : pathname.startsWith(item.href);
                  return (
                    <motion.div
                      key={item.title}
                      variants={{
                        hidden: { opacity: 0, x: -20 },
                        visible: { opacity: 1, x: 0 },
                      }}
                      className="border-b border-stone-200"
                    >
                      <Link
                        href={item.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block py-4 px-2 text-lg font-medium transition-colors"
                        style={{
                          color: isActive ? PALETTE.gold : PALETTE.darkerGreen,
                        }}
                      >
                        {item.title}
                      </Link>
                    </motion.div>
                  );
                }

                if (item.type === "mega-menu") {
                  const isExpanded = expandedMobileMenu === item.title;
                  const isActive =
                    (item.href === "/" && pathname === "/") ||
                    (item.href === "/company" && pathname === "/company") ||
                    item.subItems.some((sub) => pathname.startsWith(sub.href));

                  return (
                    <motion.div
                      key={item.title}
                      variants={{
                        hidden: { opacity: 0, x: -20 },
                        visible: { opacity: 1, x: 0 },
                      }}
                      className="border-b border-stone-200"
                    >
                      <button
                        onClick={() =>
                          setExpandedMobileMenu(isExpanded ? null : item.title)
                        }
                        className="w-full flex items-center justify-between py-4 px-2 text-lg font-medium transition-colors"
                        style={{
                          color: isActive ? PALETTE.gold : PALETTE.darkerGreen,
                        }}
                      >
                        <span>{item.title}</span>
                        <ChevronDown
                          size={20}
                          className={`transition-transform ${isExpanded ? "rotate-180" : ""
                            }`}
                        />
                      </button>

                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="pb-3 space-y-1">
                              { }
                              <Link
                                href={item.href}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="flex items-start gap-3 py-3 px-4 rounded-lg transition-colors hover:bg-stone-100 font-bold"
                                style={{
                                  backgroundColor:
                                    pathname === item.href
                                      ? `${PALETTE.gold}20`
                                      : "transparent",
                                }}
                              >
                                <span
                                  className="shrink-0 mt-0.5"
                                  style={{ color: PALETTE.darkGreen }}
                                >
                                  <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                    <rect
                                      x="3"
                                      y="3"
                                      width="18"
                                      height="18"
                                      rx="2"
                                      ry="2"
                                    />
                                    <path d="M9 3v18" />
                                    <path d="M15 3v18" />
                                    <path d="M3 9h18" />
                                    <path d="M3 15h18" />
                                  </svg>
                                </span>
                                <div className="flex-1 min-w-0">
                                  <div
                                    className="font-bold text-base mb-0.5"
                                    style={{
                                      color:
                                        pathname === item.href
                                          ? PALETTE.gold
                                          : PALETTE.darkerGreen,
                                    }}
                                  >
                                    All {item.title}
                                  </div>
                                  <p className="text-sm text-stone-600">
                                    {item.featured.description}
                                  </p>
                                </div>
                                <ChevronRight
                                  size={16}
                                  className="shrink-0 mt-1"
                                  style={{ color: PALETTE.darkGreen }}
                                />
                              </Link>

                              { }
                              {item.subItems.map((subItem) => {
                                const isSubActive = pathname.startsWith(
                                  subItem.href,
                                );
                                return (
                                  <Link
                                    key={subItem.title}
                                    href={subItem.href}
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className="flex items-start gap-3 py-3 px-4 rounded-lg transition-colors hover:bg-stone-100"
                                    style={{
                                      backgroundColor: isSubActive
                                        ? `${PALETTE.gold}20`
                                        : "transparent",
                                    }}
                                  >
                                    <span
                                      className="shrink-0 mt-0.5"
                                      style={{ color: PALETTE.darkGreen }}
                                    >
                                      {subItem.icon}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                      <div
                                        className="font-medium text-base mb-0.5"
                                        style={{
                                          color: isSubActive
                                            ? PALETTE.gold
                                            : PALETTE.darkerGreen,
                                        }}
                                      >
                                        {subItem.title}
                                      </div>
                                      <p className="text-sm text-stone-600">
                                        {subItem.description}
                                      </p>
                                    </div>
                                    <ChevronRight
                                      size={16}
                                      className="shrink-0 mt-1"
                                      style={{ color: PALETTE.darkGreen }}
                                    />
                                  </Link>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                }
                return null;
              })}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const MegaMenuContent = ({ menu }: { menu: MegaMenuItem }) => (
  <div className="w-full max-w-[600px] grid grid-cols-1 sm:grid-cols-3 gap-2 rounded-xl border border-stone-200 bg-white/95 p-2 shadow-xl backdrop-blur-md">
    <div className="sm:col-span-2 grid grid-cols-1 gap-2">
      {menu.subItems.map((item) => (
        <Link
          key={item.title}
          href={item.href}
          className="block p-4 rounded-lg hover:bg-stone-100 transition-colors group"
        >
          <div
            className="flex items-center gap-3 font-semibold mb-1 group-hover:translate-x-1 transition-transform"
            style={{ color: PALETTE.darkerGreen }}
          >
            <span className="shrink-0">{item.icon}</span>
            <span>{item.title}</span>
          </div>
          <p className="text-sm text-stone-600 ml-7">{item.description}</p>
        </Link>
      ))}
    </div>
    <div className="sm:col-span-1 h-48 sm:h-auto min-h-[200px]">
      <Link
        href={menu.featured.href}
        className="group block h-full rounded-lg overflow-hidden relative"
      >
        <Image
          src={menu.featured.image}
          alt={menu.featured.title}
          fill
          className="object-cover"
        />
        <div className="absolute inset-0 bg-linear-to-t from-black/80 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-4 text-white">
          <p className="font-semibold text-lg mb-1">{menu.featured.title}</p>
          <p className="text-sm opacity-90">{menu.featured.description}</p>
        </div>
      </Link>
    </div>
  </div>
);

export default WebAppNavbar;
